import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';

import { Dialog } from 'primereact/dialog';
import { Menu } from 'primereact/menu';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { confirmDialog } from 'primereact/confirmdialog';
import { Tooltip } from 'primereact/tooltip';

import NestedSubTask from './NestedSubTask';
import CommentsSection from './CommentsSection';
import ImageComponent from 'shared/ImageComponent';
import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';
import PriorityDropdown from 'components/UI/PriorityDropdown/PriorityDropdown';
import { ModalHeader } from 'shared/ModalHeader';
import { assignToItemTemplate } from '../../Common/PriorityDropdownTemplate';
import {
  findArrayDifferences,
  formatStageName,
  formatTaskContactList,
  getPriorityIcon,
  getUserStatusLabel,
  parseRemiderPayload,
  parseReminderData,
  setAppTitle,
} from 'utils/utils';
import { useBoardData, useBoardActions } from '../../../context/BoardContext';
import { useTaskActions, useTaskData } from '../../../context/TaskContext';
import { useTaskDetailQuery } from '../../../hooks/queries/tasks/useTaskDetailQuery';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { convertDateFromApiToUserTimezone, convertToTimezoneWithEndOfDayTime } from 'utils/utility_functions/timezone';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { useToggle } from 'hooks/useToggle';
import { getTenantIdFromLS } from 'utils/localStorageUtils';
import { generateFullName } from 'utils/generateFullNameUtils';
import { useUpdateQueryParams } from 'hooks/updateQueryParams';
import { GroupUserTooltip } from 'components/UI/GroupUserTooltip/GroupUserTooltip';
import { useBoardSection } from '../../../context/BoardSectionContext';
import { useCalendarAttendeesQuery, useCaseClientsQuery } from 'modules/TasksV2/hooks';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import Reminders from 'modules/calendar/Components/CreateEventModal/Components/EventReminders';
import constants from 'constants/index';
import TimezoneDatePicker from 'components/common/TimezoneDatePicker/TimezoneDatePicker';
import { useQueryClient } from '@tanstack/react-query';

const tenantId = getTenantIdFromLS();

const paTemplate = (option) => {
  if (option?.status === 'ARCHIVED') {
    return `${option?.practice_area_name} (Archived)`;
  }
  return option?.practice_area_name;
};

const formatTaskData = (data) => {
  const groups = data?.groups?.filter((v) => v?.group_id) || [];
  return {
    ...data,
    reminder: data?.reminder?.map((v) => ({ value: v })),
    assignTo: [
      ...data?.assign_to?.filter((v) => Boolean(v?.cognito_username || v?.firm_user_id))?.map((v) => v?.assignee_id),
      ...groups.map((v) => v?.group_id),
    ],
    reminder: parseReminderData(data?.reminder),
    sub_task: data?.sub_task?.map((st) => ({
      ...st,
      assign_to: [...(st?.assign_to?.map((v) => v?.assignee_id) || []), ...(st?.sub_task_groups?.map((group) => group?.group_id) || [])],
      assignTo: st?.assign_to,
    })),
    due_date: data?.due_date || null, // Keep as raw ISO string for TimezoneDatePicker
  };
};

const ViewTaskRefactored = () => {
  const userContext = useUserDetailsContext();
  const { addToast } = useToast();
  const { selectedTask } = useTaskData();
  const { closeViewTask } = useTaskActions();
  const { isFromCase, isFromLead, isGlobal } = useBoardSection();
  const { taskStages: taskStageState, practiceAreaList } = useBoardData();
  const { updateFullTask, deleteTask: deleteBoardTask, createTask } = useBoardActions();
  const queryClient = useQueryClient();
  const {
    task: taskDetails,
    isLoading: isLoadingTaskDetails,
    isError: isTaskError,
  } = useTaskDetailQuery(selectedTask?.task_id, {
    enabled: Boolean(selectedTask),
    onError: (error) => {
      console.error('Failed to fetch task details:', error);
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'Failed to load task details. Please try again.');
    },
  });

  // Fetch client list for non-lead tasks (wait for taskDetails to load first)
  const { clientList, isLoading: isLoadingClients } = useCaseClientsQuery({
    caseId: taskDetails?.case_id || null,
    is_mandatory_lawft_access: false,
    is_team_member_list: Boolean(taskDetails?.case_id),
    all_contacts: Boolean(taskDetails?.case_id),
    is_active: true,
    is_include_groups: true,
    active_groups: false,
    enabled: Boolean(taskDetails) && taskDetails?.is_lead !== true,
    excludeFromLoading: true,
  });

  // Fetch calendar attendees for lead tasks (wait for taskDetails to load first)
  const { attendeesList: calendarAttendees, isLoading: isLoadingCalendarAttendees } = useCalendarAttendeesQuery({
    caseId: taskDetails?.case_id,
    all_contacts: taskDetails?.case_id ? false : true,
    is_lead: taskDetails?.is_lead ? true : false,
    is_active: true,
    active_groups: true,
    enabled: Boolean(taskDetails) && taskDetails?.is_lead === true,
    excludeFromLoading: true,
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    getValues,
  } = useForm({
    defaultValues: taskDetails ? formatTaskData(taskDetails, userContext?.userDetails?.timezone) : {},
  });

  const menuRef = useRef();
  const updateQueryParams = useUpdateQueryParams();

  const [editingFields, setEditingFields] = useState([]);

  const isLead = watch('is_lead') || isFromLead;
  const notLinkedToCase = watch('not_linked_to_case');

  const [duplicate, setDuplicate] = useToggle(false);

  // Memoize the initial contact list to avoid recalculation on every render
  const initialContactList = useMemo(() => {
    if (!taskDetails) return [];

    // Use data from the appropriate query based on is_lead flag from taskDetails
    const rawUserList = taskDetails?.is_lead === true ? calendarAttendees : clientList;

    if (!rawUserList || rawUserList.length === 0) return [];

    // Apply the same filtering logic as before
    return rawUserList.filter((user) => {
      const contactUserList = user.access_level === 'client';

      const isActiveUser = user?.is_confirmed && user?.status === 'ACTIVE';
      const isArchivedConfirmed = user.res_type !== 'user-group' && (user.status === 'ARCHIVED' || user.is_confirmed === false);

      const isIndividualUser = taskDetails?.assign_to?.some((firmUser) => firmUser?.cognito_username === user?.client_cognito_username);

      const isActiveGroup = user.res_type === 'user-group' && user.status === 'ACTIVE';
      const isArchiveGroupList =
        user.res_type === 'user-group' && user?.status === 'ARCHIVED' && taskDetails?.group_list?.includes(user?.user_group_id);

      return contactUserList || isActiveUser || (isArchivedConfirmed && isIndividualUser) || isActiveGroup || isArchiveGroupList;
    });
  }, [clientList, calendarAttendees, taskDetails]);

  // Memoize the formatted and sorted contact list
  const contactList = useMemo(() => {
    if (!initialContactList || initialContactList.length === 0) return [];

    // Format and sort the contact list
    // const formattedList = formatContactList(initialContactList);
    const formattedList = formatTaskContactList(initialContactList);
    return formattedList?.map((item) => {
      // if (item?.code === 'Users') {
      if (item?.code === 'Firm Users') {
        return {
          ...item,
          items: item.items.sort((a, b) => {
            const nameA = (a?.first_name || a?.assignee_name || '').toLowerCase();
            const nameB = (b?.first_name || b?.assignee_name || '').toLowerCase();
            return nameA.localeCompare(nameB);
          }),
        };
      } else if (item?.code === 'Groups') {
        return {
          ...item,
          items: item.items.sort((a, b) => (a.group_name?.toLowerCase() || '').localeCompare(b.group_name?.toLowerCase() || '')),
        };
      }
      return item;
    });
  }, [initialContactList]);

  // Update form when task details are loaded
  useEffect(() => {
    if (taskDetails && userContext?.userDetails?.timezone) {
      const formattedData = formatTaskData(taskDetails, userContext.userDetails.timezone);
      reset(formattedData);
    }
  }, [taskDetails, userContext?.userDetails?.timezone, reset]);

  useEffect(() => {
    if (taskDetails) {
      setAppTitle('Tasks', taskDetails?.task_name);
    }

    return () => {
      setAppTitle('Tasks');
      updateQueryParams({ id: null });
    };
  }, [taskDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    //PrimeReactMultiSelect: Empty label chips showing.
    const dropdownClasses = document?.querySelectorAll('.p-multiselect-token');
    for (const dropdownElement of dropdownClasses) {
      const dropLabelElement = dropdownElement?.querySelector('.p-multiselect-token-label')?.innerHTML;

      if (dropLabelElement?.trim()?.length === 0) {
        dropdownElement.style.display = 'none';
      }
    }
  }, [contactList, editingFields]);

  const onSubmit = (formData) => {
    const firmUsers = contactList?.find((v) => v.code === 'Firm Users')?.items || [];
    const contacts = contactList?.find((v) => v.code === 'Contacts')?.items || [];
    const allUsers = [...firmUsers, ...contacts];

    const filteredSubTask = formData?.sub_task
      ?.filter((task) => Boolean(task.sub_task_description))
      ?.map((st) => {
        return {
          ...st,
          assign_to:
            allUsers
              ?.filter((user) => st?.assign_to?.includes(user?.assignee_id))
              ?.map((user) => ({
                assignee_id: user?.client_cognito_username || user?.firm_user_id || user?.contact_id,
                assignee_name: user?.assignee_name || generateFullName(user),
                email: user?.email,
                access_level: user?.access_level,
                cognito_username: user?.client_cognito_username,
                contact_id: user?.contact_id,
              })) || [],

          sub_task_groups:
            contactList
              ?.find((v) => v.code === 'Groups')
              ?.items?.filter((group) => st?.assign_to?.includes(group?.group_id))
              ?.map((group) => ({
                group_name: group?.group_name,
                group_id: group?.group_id,
                sk: group?.sk,
                assignee_id: group?.assignee_id,
                assignee_name: group?.assignee_name,
              })) || [],

          assignTo: undefined,
        };
      });

    const practiceArea = Array.isArray(formData?.practice_area) ? formData.practice_area[0] : formData?.practice_area ?? '';

    const toDoId = taskStageState?.find((stage) => stage?.task_stage_name === 'to_do')?.task_stage_id;

    let dataToSent = {
      case_id: formData.case_id,
      pk: formData.pk,
      sk: formData.sk,
      assign_to: formData?.assign_to,
      priority: formData.priority,
      practice_area: practiceArea,
      task_stage: formData.task_stage ?? toDoId,
      accessible_list: formData?.assign_to?.map((v) => v.assignee_id),
      task_name: formData.task_name,
      reminder: parseRemiderPayload(formData?.reminder),
      due_date: formData?.due_date,
      case_name: formData?.case_name,
      description: formData?.description,
      sub_task: filteredSubTask,
      groups: formData?.groups?.map((group) => {
        const { firm_user_data, ...groupWithoutFirmUserData } = group;
        return groupWithoutFirmUserData;
      }),
      is_lead: isFromLead ? isFromLead : formData?.is_lead,
    };

    const oldList = taskDetails?.sub_task || [];
    const newList = filteredSubTask || [];

    let statusChanged = false;

    // If count differs → added/removed → do NOT update stage
    if (oldList.length === newList.length) {
      for (let i = 0; i < oldList.length; i++) {
        const oldStatus = oldList[i]?.sub_task_status;
        const newStatus = newList[i]?.sub_task_status;

        if (oldStatus !== newStatus) {
          statusChanged = true;
          break;
        }
      }
    }

    if (statusChanged) {
      const totalCompleted = newList.filter((st) => st?.sub_task_status === true);
      const totalCount = newList.length || 0;

      if (totalCompleted.length === 0) {
        dataToSent.task_stage = taskDetails?.dynamic_task_stage || toDoId;
      } else if (totalCompleted.length < totalCount) {
        const inProgressId = taskStageState?.find((stage) => stage?.task_stage_name === 'in_progress')?.task_stage_id;
        dataToSent.task_stage = inProgressId;
      } else if (totalCompleted.length === totalCount && totalCount > 0) {
        const doneId = taskStageState?.find((stage) => stage?.task_stage_name === 'done')?.task_stage_id;
        dataToSent.task_stage = doneId;
      }
    }

    // --------------------------------------------------------------
    // 🔥 API CALL
    // --------------------------------------------------------------
    if (!duplicate) {
      // Update existing task
      updateFullTask(taskDetails?.task_id, dataToSent, false, isFromLead || formData?.is_lead)
        .then((result) => {
          if (result.success) {
            addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.TASK_UPDATED_SUCCESSFULLY);
            onHide();
          } else {
            addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.TASK_UPDATED_FAILED);
          }
        })
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['agendaTasks', 'column', taskDetails?.task_stage] });
        })
        .catch(() => {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.TASK_UPDATED_FAILED);
        });
    } else {
      delete dataToSent.sk;
      delete dataToSent.pk;

      // Create duplicate task
      createTask(dataToSent, isFromLead || formData?.is_lead)
        .then((result) => {
          if (result.success) {
            addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.TASK_CREATED_SUCCESSFULLY);
            onHide();
          } else {
            addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.TASK_UPDATED_FAILED);
          }
        })
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['agendaTasks', 'column', taskDetails?.task_stage] });
        })
        .catch(() => {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.TASK_UPDATED_FAILED);
        });
    }
  };

  const handleFieldClick = (fieldName) => {
    if (!editingFields.includes(fieldName)) {
      setEditingFields([...editingFields, fieldName]);
    }
  };

  const onHide = () => {
    setDuplicate(false);
    closeViewTask();
  };

  const updateAssignToField = (newArray) => {
    const oldArray = getValues('assignTo');
    const assign_to = getValues('assign_to') || [];

    const groups = getValues('groups') || [];
    const { added, removed } = findArrayDifferences(oldArray, newArray);
    const contacts = contactList.reduce((arr, val) => [...arr, ...val.items], []);

    if (added?.[0]) {
      const addedContact = contacts?.find((v) => v?.assignee_id === added?.[0] || v?.group_id?.includes(added?.[0]));

      if (addedContact?.group_id) {
        const { group_name, user_group_id, sk } = addedContact;
        setValue('groups', [
          ...groups,
          {
            group_name,
            group_id: user_group_id,
            sk: sk,
          },
        ]);
      } else if (addedContact?.assignee_id) {
        const { assignee_id, assignee_name, contact_id, email, client_cognito_username, firm_user_id, access_level } = addedContact;
        const contactIndex = assign_to?.findIndex((c) => c?.assignee_id === addedContact?.assignee_id);

        if (contactIndex !== -1) {
          setValue('assign_to', assign_to);
        } else {
          setValue('assign_to', [
            ...assign_to,
            {
              assignee_id,
              assignee_name,
              cognito_username: client_cognito_username,
              contact_id,
              firm_user_id,
              email,
              access_level: access_level,
            },
          ]);
        }
      }
    }
    if (removed?.[0]) {
      const removedContact = contacts?.find((v) => v?.assignee_id === removed?.[0] || v?.group_id?.includes(removed?.[0]));
      if (removedContact?.group_id) {
        setValue(
          'groups',
          groups?.filter((g) => g.group_id !== removedContact.group_id)
        );
      } else if (removedContact?.assignee_id) {
        const newArray = [];

        assign_to.forEach((item) => {
          if (
            removedContact?.assignee_id === item.assignee_id &&
            !(removedContact?.res_type === 'user-group' && item?.user_group_id === removedContact?.group_id)
          ) {
            // Skip this item, effectively removing it
            return;
          } else {
            newArray.push(item);
          }
        });
        setValue('assign_to', newArray);
      }
    }
  };

  const handleDeleteTask = () => {
    const taskId = taskDetails?.task_id;
    const taskCaseId = taskDetails?.case_id;
    const taskPk = taskDetails?.pk;

    deleteBoardTask(taskId, taskCaseId, taskPk, isFromLead || isLead)
      .then((result) => {
        if (result.success) {
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, 'Task deleted successfully');
          onHide();
        } else {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'Failed to delete task');
        }
      })
      .catch(() => {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'Failed to delete task');
      });
  };

  const items = [
    {
      label: 'Delete',
      command: () => {
        confirmDialog({
          message: 'Do you want to delete this task?',
          header: 'Delete Confirmation',
          icon: 'pi pi-info-circle',
          acceptClassName: 'p-button-danger',
          accept: handleDeleteTask,
        });
      },
    },
    {
      label: 'Duplicate',
      command: () => {
        handleFieldClick('task_name');
        setValue('task_name', getValues('task_name') + ' - Duplicate');
        setDuplicate(true);
      },
    },
  ];

  const RequiredValidation = ({ field }) => {
    return (
      <>{errors?.[field]?.type === 'required' && <InputErrorMessage>{errors?.[field]?.message || 'Field Required'}</InputErrorMessage>}</>
    );
  };

  if (!selectedTask) return null;

  if (isLoadingTaskDetails) {
    return (
      <Dialog
        visible={true}
        onHide={onHide}
        header={ModalHeader}
        style={{ width: '80vw' }}
        footer={<></>}
        className="new-task-popup view-task-modal"
        contentStyle={{ overflowY: 'hidden' }}
      >
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '3rem' }}></i>
        </div>
      </Dialog>
    );
  }

  if (isTaskError) {
    return (
      <Dialog
        visible={true}
        onHide={onHide}
        header={ModalHeader}
        style={{ width: '80vw' }}
        footer={<></>}
        className="new-task-popup view-task-modal"
        contentStyle={{ overflowY: 'hidden' }}
      >
        <div className="d-flex justify-content-center align-items-center flex-column" style={{ minHeight: '400px' }}>
          <i className="pi pi-exclamation-triangle" style={{ fontSize: '3rem', color: 'var(--red-500)' }}></i>
          <p className="mt-3">Failed to load task details. Please try again.</p>
          <Button label="Close" onClick={onHide} className="p-button-secondary mt-2" />
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      visible={true}
      onHide={onHide}
      header={ModalHeader}
      style={{ width: '80vw' }}
      footer={<></>}
      className="new-task-popup view-task-modal"
      contentStyle={{ overflowY: 'hidden' }}
    >
      <div className="d-flex flex-lg-row flex-column" style={{ height: '70vh', maxHeight: '70vh' }}>
        <div className="col-lg-8 col-md-8 col-12 thin-scrollbar" style={{ height: '100%', overflowY: 'auto' }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="d-flex flex-column thin-scrollbar pe-3">
              <div className="mb-3">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="col-lg-10 col-10" onClick={() => handleFieldClick('task_name')}>
                    <div className="modal-heading F-size16 text-bold">
                      {editingFields.includes('task_name') ? (
                        <TextSnippetWrapper
                          type="text"
                          className="border border-0 w-100 input-shadow"
                          placeholder="Enter Task Title"
                          {...register('task_name', { required: 'Task Title is required' })}
                        />
                      ) : (
                        taskDetails?.task_name
                      )}
                    </div>
                    <RequiredValidation field={'task_name'} />
                  </div>
                  <div className="col-lg-2 col-2 d-flex justify-content-end align-items-center">
                    <div className="p-d-flex p-ai-center">
                      <i className="pi pi-ellipsis-v pointer" onClick={(e) => menuRef.current.toggle(e)} id="threedots" />
                      <Menu model={items} popup ref={menuRef} id="popup_menu" />
                    </div>
                  </div>
                </div>
                {!(isFromCase || isFromLead) && isLead && (
                  <div className="p-tag">
                    <span className="p-tag-value">{isLead ? 'Lead' : 'Case'}</span>
                  </div>
                )}
              </div>
              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-md-4 col-12 p-medium">
                  Status <span className="alert-star">*</span>
                </div>
                <div className="col-md-8 col-12 edit-field" onClick={() => handleFieldClick('task_stage')}>
                  {editingFields.includes('task_stage') ? (
                    <Controller
                      name="task_stage"
                      control={control}
                      rules={{ required: 'Status is Required' }}
                      render={({ field }) => (
                        <Dropdown
                          options={taskStageState || []}
                          optionLabel={(v) => formatStageName(v?.task_stage_name)}
                          className="w-100 input-shadow F-size14 input-height"
                          optionValue="task_stage_id"
                          id={field.name}
                          value={field.value}
                          onChange={(e) => field.onChange(e.value)}
                        />
                      )}
                    />
                  ) : (
                    formatStageName(taskDetails?._task_stage_)
                  )}
                </div>
                <RequiredValidation field={'task_stage'} />
              </div>
              {taskDetails?.case_name && (
                <div className="d-flex align-items-center py-2 flex-wrap">
                  <div className="col-md-4 col-12 p-medium">Case Name</div>
                  <Link
                    to={`/${isLead ? 'leads' : 'cases'}/${taskDetails?.case_id || ''}`}
                    className={`col-md-8 col-12  ${
                      taskDetails?.case_name && taskDetails?.case_id && !Boolean(isFromCase) && !isFromLead
                        ? 'pointer text-primary-dark redirection-link-no-text-transform'
                        : ''
                    }`}
                  >
                    {taskDetails?.case_name}
                  </Link>
                </div>
              )}

              <div className="d-flex py-2 flex-wrap">
                <div className="col-md-4 col-12 p-medium mt-1">
                  Assignees <span className="alert-star">*</span>
                </div>
                <div className="col-md-8 col-12">
                  {editingFields.includes('assign_to') ? (
                    <Controller
                      name="assignTo"
                      control={control}
                      rules={{ required: 'Assign To is Required' }}
                      render={({ field }) => (
                        <MultiSelect
                          options={!notLinkedToCase ? contactList : formatTaskContactList(initialContactList)}
                          className="w-100 input-shadow F-size14 input-height"
                          filter
                          filterPlaceholder="Search"
                          placeholder="Select Assignee"
                          display="chip"
                          optionLabel="assignee_name"
                          optionValue={(v) => v?.assignee_id}
                          itemTemplate={assignToItemTemplate}
                          value={field.value}
                          onChange={(e) => {
                            updateAssignToField(e.value);
                            field.onChange(e.value);
                          }}
                          showSelectAll={false}
                          id={field.name}
                          resetFilterOnHide
                          optionGroupLabel="assignee_name"
                          optionGroupChildren="items"
                        />
                      )}
                    />
                  ) : (
                    <>
                      {taskDetails?.assign_to
                        ?.filter((v) => v?.cognito_username || v?.firm_user_id)
                        .map((item, i) => {
                          return (
                            <div
                              className="d-flex align-items-center py-1 p-avatar-clickable"
                              key={i}
                              onClick={() => handleFieldClick('assign_to')}
                            >
                              <ImageComponent
                                filePath={
                                  item?.contact_id || item?.firm_user_id
                                    ? `${tenantId}/${item?.contact_id ? item?.contact_id : item?.firm_user_id}/profile_image`
                                    : null
                                }
                                fileName={item.assignee_name}
                                className="me-2"
                                style={{
                                  backgroundColor: '#2196F3',
                                  color: '#ffffff',
                                  border: `2px solid ${item?.calendar_color ?? '#ffff'}`,
                                }}
                              />
                              {item?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
                              {generateFullName(item) || item.assignee_name}
                              {getUserStatusLabel(item)}
                              <span>{item.assignee_id === taskDetails?.created_by ? ' (Created By)' : ''}</span>
                            </div>
                          );
                        })}
                      {taskDetails?.groups?.map((item, i) => {
                        return (
                          <div
                            className="d-flex align-items-center py-1 p-avatar-clickable"
                            key={i}
                            onClick={() => handleFieldClick('assign_to')}
                          >
                            <ImageComponent
                              fileName={item.group_name}
                              className="me-2"
                              style={{ backgroundColor: '#2196F3', color: '#ffffff', border: `2px solid #ffff` }}
                            />

                            <span className={`groups-tooltip-${i}`}>
                              {item.group_name} <span>{item.group_status === 'ARCHIVED' ? '   (Archived)' : ''}</span>
                            </span>

                            <Tooltip target={`.groups-tooltip-${i}`} position="top">
                              {GroupUserTooltip(item)}
                            </Tooltip>
                          </div>
                        );
                      })}
                    </>
                  )}
                  <RequiredValidation field={'assignTo'} />
                </div>
              </div>

              <div className="d-flex align-items-top py-2 flex-wrap">
                <div className="col-md-4 col-12 p-medium">Sub Tasks</div>
                <div className="col-md-8 col-12">
                  <NestedSubTask {...{ control, register, errors, contactList }} />
                </div>
              </div>

              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-md-4 col-12 p-medium">
                  Priority <span className="alert-star">*</span>
                </div>
                <div className="col-md-8 col-12">
                  {editingFields.includes('priority') ? (
                    <Controller
                      name="priority"
                      control={control}
                      rules={{ required: 'Priority is Required' }}
                      render={({ field }) => (
                        <PriorityDropdown
                          className="input-shadow w-100 input-height"
                          filter
                          id={field.name}
                          value={field.value}
                          onChange={(e) => field.onChange(e.value)}
                        />
                      )}
                    />
                  ) : (
                    <div onClick={() => handleFieldClick('priority')}>
                      <i className={`${getPriorityIcon(taskDetails?.priority)} pe-1`}></i>
                      {taskDetails?.priority}
                    </div>
                  )}
                  <RequiredValidation field={'priority'} />
                </div>
              </div>

              {!Boolean(taskDetails?.case_id) && (
                <div className="d-flex align-items-center py-2 flex-wrap">
                  <div className="col-md-4 col-12 p-medium">Practice Area</div>
                  <div className="col-md-8 col-12" onClick={() => handleFieldClick('practice_area')}>
                    {editingFields.includes('practice_area') ? (
                      <Controller
                        name="practice_area"
                        control={control}
                        render={({ field }) => {
                          const selectedValue = Array.isArray(field.value) ? field.value[0] : field.value;

                          return (
                            <Dropdown
                              className="input-shadow w-100 input-height"
                              filter
                              id={field.name}
                              value={selectedValue}
                              options={practiceAreaList?.filter(
                                (option) => field?.value?.includes(option.sk) || option.status === 'ACTIVE'
                              )}
                              optionLabel="practice_area_name"
                              // optionValue="sk"
                              optionValue="practice_area_id"
                              onChange={(e) => field.onChange([e.value])} // Convert back to array for consistency
                              showClear
                              optionDisabled={(item) => item?.status !== 'ACTIVE'}
                              itemTemplate={paTemplate}
                            />
                          );
                        }}
                      />
                    ) : (
                      taskDetails?._practice_area_ || 'N/A'
                    )}
                  </div>
                </div>
              )}

              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-md-4 col-12 p-medium">
                  Due Date <span className="alert-star">*</span>
                </div>
                <div className="col-md-8 col-12" onClick={() => handleFieldClick('due_date')}>
                  {editingFields.includes('due_date') ? (
                    <Controller
                      name="due_date"
                      control={control}
                      rules={{ required: 'Due Date is Required' }}
                      render={({ field }) => (
                        <TimezoneDatePicker
                          className="filter-calender input-shadow"
                          dateFormat="mm/dd/yy"
                          showIcon
                          monthNavigator
                          yearNavigator
                          yearRange={`${new Date().getFullYear() - 100}:${new Date().getFullYear() + 50}`}
                          id={field.name}
                          value={field.value}
                          isEndOfDay={true}
                          onChange={field.onChange}
                          timezone={userContext?.userDetails?.timezone}
                        />
                      )}
                    />
                  ) : (
                    convertDateFromApiToUserTimezone(
                      taskDetails?.due_date,
                      userContext?.userDetails?.timezone,
                      constants.month_date_year_format
                    )
                  )}
                  <RequiredValidation field={'due_date'} />
                </div>
              </div>

              <div className="d-flex align-items-center py-2 flex-wrap">
                <Reminders control={control} watch={watch} isGlobal={isGlobal} />
              </div>
              <div className="d-flex align-items-start py-2 flex-wrap">
                <div className="col-md-4 col-12 p-medium">Description</div>
                <div className="col-md-8 col-12">
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <TextSnippetWrapper type="textarea" rows="5" className="w-100 input-shadow" id="description" {...field} />
                    )}
                  />
                </div>
              </div>
            </div>
            <div className="footer-btn">
              <Button type="reset" className="me-2 btn p-button-secondary outline" onClick={onHide}>
                Cancel
              </Button>
              <Button className="p-button-secondary me-2" type="submit">
                {duplicate ? 'Create' : 'Save'}
              </Button>
            </div>
          </form>
        </div>

        <div className="col-lg-4 col-md-4 col-12 border-start ps-3" style={{ height: '100%', overflowY: 'auto' }}>
          <CommentsSection taskId={taskDetails?.task_id} caseId={taskDetails?.case_id} userContext={userContext} />
        </div>
      </div>
      <style>{`
        @media (max-width: 991.98px) {
          .view-task-modal .p-dialog-content > .d-flex.flex-lg-row.flex-column {
            max-height: 70vh !important;
            overflow-y: auto !important;
          }
          .view-task-modal .col-lg-8,
          .view-task-modal .col-lg-4 {
            max-width: 100%;
            flex: 0 0 100%;
            border-left: none !important;
            padding-left: 0 !important;
            margin-left: 0 !important;
            height: auto !important;
            overflow-y: visible !important;
            max-height: fit-content !important;
          }
        }
        @media (min-width: 992px) {
          .view-task-modal .p-dialog-content > .d-flex.flex-lg-row.flex-column {
            height: 70vh !important;
            max-height: 70vh !important;
            overflow: hidden !important;
          }
          .view-task-modal .col-lg-8,
          .view-task-modal .col-lg-4 {
            height: 100% !important;
            overflow-y: auto !important;
          }
        }
      `}</style>
    </Dialog>
  );
};

export default ViewTaskRefactored;
